/**
 * SIP Error codes and their user-friendly messages
 */
export interface SipErrorInfo {
  title: string;
  message: string;
  action?: string;
  helpLink?: string;
  severity: 'error' | 'warning' | 'info';
}

/**
 * Maps technical SIP errors to user-friendly messages with actionable guidance
 */
export function getSipErrorInfo(error: string | Error): SipErrorInfo {
  const errorString = typeof error === 'string' ? error.toLowerCase() : error.message.toLowerCase();

  // Microphone/Permission Errors
  if (errorString.includes('notallowederror') ||
      errorString.includes('permission denied') ||
      errorString.includes('microphone')) {
    return {
      title: 'Microphone Access Blocked',
      message: 'Your browser is blocking microphone access. Please enable it to make calls.',
      action: 'Click the 🔒 lock icon in your browser address bar and allow microphone access, then refresh the page.',
      severity: 'error'
    };
  }

  // Network/Connection Errors
  if (errorString.includes('websocket') ||
      errorString.includes('network') ||
      errorString.includes('connection failed')) {
    return {
      title: 'Connection Problem',
      message: 'Unable to connect to the phone server. Please check your internet connection.',
      action: 'Check your WiFi or network connection and try again.',
      severity: 'error'
    };
  }

  // Registration Errors
  if (errorString.includes('registration failed') ||
      errorString.includes('401') ||
      errorString.includes('unauthorized')) {
    return {
      title: 'Login Failed',
      message: 'Unable to connect to your phone account. Your credentials may be incorrect.',
      action: 'Please contact your administrator to verify your phone credentials.',
      severity: 'error'
    };
  }

  // Call Failed - Busy
  if (errorString.includes('486') || errorString.includes('busy')) {
    return {
      title: 'Line Busy',
      message: 'The person you are calling is currently on another call.',
      action: 'Please try again later.',
      severity: 'warning'
    };
  }

  // Call Failed - No Answer
  if (errorString.includes('408') ||
      errorString.includes('timeout') ||
      errorString.includes('no answer')) {
    return {
      title: 'No Answer',
      message: 'The person you called did not answer.',
      action: 'You may try calling again or leave a voicemail.',
      severity: 'info'
    };
  }

  // Call Failed - Not Found
  if (errorString.includes('404') || errorString.includes('not found')) {
    return {
      title: 'Number Not Found',
      message: 'The phone number or extension you dialed could not be found.',
      action: 'Please check the number and try again.',
      severity: 'error'
    };
  }

  // Call Failed - Declined
  if (errorString.includes('603') ||
      errorString.includes('decline') ||
      errorString.includes('rejected')) {
    return {
      title: 'Call Declined',
      message: 'The person you called declined the call.',
      action: 'They may be busy. Try again later.',
      severity: 'info'
    };
  }

  // Call Failed - Service Unavailable
  if (errorString.includes('503') || errorString.includes('unavailable')) {
    return {
      title: 'Service Unavailable',
      message: 'The phone service is temporarily unavailable.',
      action: 'Please try again in a few moments or contact support.',
      severity: 'error'
    };
  }

  // Audio Device Errors
  if (errorString.includes('audio') ||
      errorString.includes('speaker') ||
      errorString.includes('playback')) {
    return {
      title: 'Audio Device Problem',
      message: 'There is an issue with your audio device (speaker or microphone).',
      action: 'Check your speaker/microphone settings and ensure they are properly connected.',
      severity: 'error'
    };
  }

  // Browser Not Supported
  if (errorString.includes('not supported') || errorString.includes('webrtc')) {
    return {
      title: 'Browser Not Supported',
      message: 'Your browser does not support the required features for calling.',
      action: 'Please use a modern browser like Chrome, Firefox, or Edge.',
      severity: 'error'
    };
  }

  // Generic/Unknown Error
  return {
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred with the phone system.',
    action: 'Please try again. If the problem persists, contact support.',
    severity: 'error'
  };
}

/**
 * Connection status messages
 */
export function getConnectionStatusMessage(status: string): { message: string; severity: 'success' | 'warning' | 'error' | 'info' } {
  switch (status.toLowerCase()) {
    case 'connected':
    case 'registered':
      return { message: 'Connected and ready to make calls', severity: 'success' };

    case 'connecting':
    case 'registering':
      return { message: 'Connecting to phone server...', severity: 'info' };

    case 'disconnected':
      return { message: 'Disconnected from phone server', severity: 'warning' };

    case 'registration_failed':
      return { message: 'Unable to connect. Please check your credentials.', severity: 'error' };

    case 'unregistered':
      return { message: 'Not connected to phone server', severity: 'warning' };

    default:
      return { message: status, severity: 'info' };
  }
}
